import React, { useState, useRef, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { 
  Bot, Send, Mic, MicOff, Brain, Users, Target, 
  Calendar, MessageSquare, TrendingUp, Lightbulb,
  Clock, User, Building, MapPin, Star, Check, 
  Clock as ClockIcon, Reply
} from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useNetworking } from '@/contexts/NetworkingContext';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';

interface ChatMessage {
  id: string;
  type: 'user' | 'assistant';
  content: string;
  timestamp: Date;
  suggestions?: string[];
  contextData?: {
    members?: any[];
    events?: any[];
    opportunities?: any[];
    relevantMembers?: any[];
    networkAnalysis?: {
      totalConnections: number;
      networkStrength: number;
      skillsGaps: any[];
    };
    upcomingEvents?: any[];
  };
}

interface NetworkingInsight {
  type: 'connection' | 'opportunity' | 'timing' | 'strategy';
  title: string;
  description: string;
  actionable: boolean;
  confidence: number;
}

const NetworkingAIAssistant = () => {
  const { user, member } = useAuth();
  const { sendConnectionRequest, connections, connectionRequests } = useNetworking();
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [isRecording, setIsRecording] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [connectingMembers, setConnectingMembers] = useState<Set<string>>(new Set());
  const [userJustSent, setUserJustSent] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Initialize with personalized welcome message - STABLE VERSION
  useEffect(() => {
    const initializeAssistant = async () => {
      if (!user?.id || messages.length > 0) return; // Prevent reinitializing if messages exist
      
      try {
        const userName = member?.firstname || 'Membre';

        const welcomeMessage: ChatMessage = {
          id: '1',
          type: 'assistant',
          content: `Bonjour ${userName} ! Je peux vous aider à trouver des collaborateurs dans votre réseau. Que recherchez-vous ?`,
          timestamp: new Date(),
          suggestions: [
            "Analyser les opportunités de collaboration",
            "Trouver quelqu'un qui cherche mes compétences",
            "Qui peut compléter mes compétences ?"
          ]
        };
        
        setMessages([welcomeMessage]);
      } catch (error) {
        console.error('Erreur lors de l\'initialisation:', error);
        // Message de fallback
        const welcomeMessage: ChatMessage = {
          id: '1',
          type: 'assistant',
          content: `Bonjour ${member?.firstname || 'Membre'} ! Je peux vous aider à trouver des collaborateurs dans votre réseau. Que recherchez-vous ?`,
          timestamp: new Date(),
          suggestions: [
            "Analyser les opportunités de collaboration",
            "Trouver quelqu'un qui cherche mes compétences",
            "Qui peut compléter mes compétences ?"
          ]
        };
        setMessages([welcomeMessage]);
      }
    };

    initializeAssistant();
  }, [user?.id]); // Only depend on user.id, not member

  // Real networking insights based on data
  const [networkingInsights, setNetworkingInsights] = useState<NetworkingInsight[]>([]);

  // Load real insights when component mounts - STABLE VERSION
  useEffect(() => {
    const loadInsights = async () => {
      if (!user?.id || networkingInsights.length > 0) return; // Prevent reloading if insights exist
      
      try {
        // Get real insights from the database
        const insights = await generateRealInsights();
        setNetworkingInsights(insights);
      } catch (error) {
        console.error('Error loading insights:', error);
        // Fallback to default insight
        setNetworkingInsights([{
          type: 'strategy',
          title: 'Complétez votre profil',
          description: 'Ajoutez vos compétences et besoins pour recevoir des recommandations personnalisées.',
          actionable: true,
          confidence: 95
        }]);
      }
    };

    loadInsights();
  }, [user?.id]); // Only depend on user.id

  const generateRealInsights = async (): Promise<NetworkingInsight[]> => {
    const insights: NetworkingInsight[] = [];

    // Check profile completion
    if (!member?.skills || member.skills.length === 0) {
      insights.push({
        type: 'strategy',
        title: 'Profil incomplet détecté',
        description: 'Ajoutez vos compétences pour débloquer des recommandations personnalisées.',
        actionable: true,
        confidence: 95
      });
    }

    // Check for pending connection requests
    const { data: pendingRequests } = await supabase
      .from('connection_requests')
      .select('count', { count: 'exact' })
      .eq('requested_id', user.id)
      .eq('status', 'pending');

    if (pendingRequests && pendingRequests.length > 0) {
      insights.push({
        type: 'connection',
        title: 'Demandes en attente',
        description: `Vous avez des demandes de connexion en attente. Examinez-les pour étendre votre réseau.`,
        actionable: true,
        confidence: 90
      });
    }

    // Check recent searches for opportunities
    const { data: recentSearches } = await supabase
      .from('member_searches')
      .select('search_term')
      .eq('user_id', user.id)
      .order('created_at', { ascending: false })
      .limit(5);

    if (recentSearches && recentSearches.length > 0) {
      const topSearchTerm = recentSearches[0].search_term;
      insights.push({
        type: 'opportunity',
        title: 'Recherche récente',
        description: `Vous cherchez des profils "${topSearchTerm}". Voulez-vous que je vous aide à les trouver ?`,
        actionable: true,
        confidence: 85
      });
    }

    return insights.slice(0, 3);
  };

  const handleSendMessage = async () => {
    if (!inputValue.trim() || !user?.id) return;

    const userMessage: ChatMessage = {
      id: Date.now().toString(),
      type: 'user',
      content: inputValue,
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setUserJustSent(true); // Mark that user just sent a message
    const currentMessage = inputValue;
    setInputValue('');
    setIsLoading(true);

    try {
      console.log('🤖 Calling real AI assistant...');
      
      // Call the real AI networking function
      const { data, error } = await supabase.functions.invoke('networking-ai', {
        body: { 
          message: currentMessage,
          userId: user.id
        }
      });

      if (error) {
        console.error('AI Error:', error);
        throw error;
      }

      const assistantMessage: ChatMessage = {
        id: (Date.now() + 1).toString(),
        type: 'assistant',
        content: data.response || "Désolé, je n'ai pas pu traiter votre demande.",
        timestamp: new Date(),
        suggestions: generateSmartSuggestions(currentMessage, data.contextData),
        contextData: data.contextData
      };

      setMessages(prev => [...prev, assistantMessage]);
    } catch (error) {
      console.error('Error calling AI assistant:', error);
      
      const errorMessage: ChatMessage = {
        id: (Date.now() + 1).toString(),
        type: 'assistant',
        content: "Désolé, je rencontre un problème technique. Pouvez-vous réessayer ?",
        timestamp: new Date()
      };
      
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const generateSmartSuggestions = (originalMessage: string, contextData: any): string[] => {
    const suggestions: string[] = [];
    
    const genericSuggestions = [
      "Analyser les opportunités de collaboration",
      "Trouver quelqu'un qui cherche mes compétences",
      "Qui peut compléter mes compétences ?"
    ];
    
    // Suggestions contextuelles basées sur les résultats
    if (contextData?.relevantMembers?.length > 0) {
      suggestions.push("Comment puis-je collaborer avec eux ?");
      suggestions.push("Qui peut compléter mes compétences ?");
    }
    
    if (contextData?.networkAnalysis) {
      suggestions.push("Analyser les opportunités de collaboration");
      suggestions.push("Trouver quelqu'un qui cherche mes compétences");
    }
    
    if (contextData?.upcomingEvents?.length > 0) {
      suggestions.push("Comment m'inscrire à ces événements ?");
      suggestions.push("Trouver quelqu'un qui cherche mes compétences");
    }
    
    // Ajouter des suggestions génériques pour compléter jusqu'à 3
    for (const genericSuggestion of genericSuggestions) {
      if (suggestions.length >= 3) break;
      if (!suggestions.includes(genericSuggestion)) {
        suggestions.push(genericSuggestion);
      }
    }
    
    return suggestions.slice(0, 3);
  };


  const handleSuggestionClick = (suggestion: string) => {
    setInputValue(suggestion);
  };

  // Get connection status for a member
  const getConnectionStatus = (memberUserId: string) => {
    if (!user?.id) return 'can_connect';
    
    // Check if already connected
    const isConnected = connections.some(conn => conn.id === memberUserId);
    if (isConnected) return 'connected';
    
    // Check for pending request sent by current user
    const sentRequest = connectionRequests.find(
      req => req.requester_id === user.id && 
             req.requested_id === memberUserId && 
             req.status === 'pending'
    );
    if (sentRequest) return 'request_sent';
    
    // Check for pending request received by current user
    const receivedRequest = connectionRequests.find(
      req => req.requested_id === user.id && 
             req.requester_id === memberUserId && 
             req.status === 'pending'
    );
    if (receivedRequest) return 'request_received';
    
    return 'can_connect';
  };

  const handleConnect = async (memberId: string, memberName: string) => {
    try {
      setConnectingMembers(prev => new Set(prev).add(memberId));
      
      const success = await sendConnectionRequest(memberId);
      
      if (success) {
        toast.success(`Demande de connexion envoyée à ${memberName}`);
      } else {
        toast.error("Erreur lors de l'envoi de la demande");
      }
    } catch (error) {
      console.error('Error connecting:', error);
      toast.error("Erreur lors de la connexion");
    } finally {
      setConnectingMembers(prev => {
        const newSet = new Set(prev);
        newSet.delete(memberId);
        return newSet;
      });
    }
  };

  const handleVoiceToggle = () => {
    setIsRecording(!isRecording);
    // Voice recording logic would go here
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    // Only scroll when user sends a message, not when AI responds
    if (messages.length > 1 && userJustSent) {
      scrollToBottom();
      setUserJustSent(false);
    }
  }, [messages.length, userJustSent]);

  const getInsightIcon = (type: string) => {
    switch (type) {
      case 'timing': return <Clock className="h-4 w-4 text-blue-500" />;
      case 'opportunity': return <Target className="h-4 w-4 text-green-500" />;
      case 'connection': return <Users className="h-4 w-4 text-purple-500" />;
      case 'strategy': return <TrendingUp className="h-4 w-4 text-orange-500" />;
      default: return <Lightbulb className="h-4 w-4 text-yellow-500" />;
    }
  };

  return (
    <div className="h-screen bg-background overflow-auto">
      <div className="grid gap-6 lg:grid-cols-3 p-6 h-full">
        {/* Main Chat Interface */}
        <div className="lg:col-span-2 flex">
          <Card className="flex-1 flex flex-col">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2">
              <Bot className="h-5 w-5 text-primary" />
              Assistant IA Networking
            </CardTitle>
            <CardDescription>
              Votre copilot personnel pour optimiser votre networking
            </CardDescription>
          </CardHeader>
          
          <CardContent className="flex-1 flex flex-col p-0">
            {/* Messages */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4">
              {messages.map((message) => (
                <div
                  key={message.id}
                  className={`flex gap-3 ${message.type === 'user' ? 'flex-row-reverse' : ''}`}
                >
                  <Avatar className="h-8 w-8 flex-shrink-0">
                    {message.type === 'assistant' ? (
                      <div className="bg-primary/10 text-primary rounded-full w-full h-full flex items-center justify-center">
                        <Bot className="h-4 w-4" />
                      </div>
                    ) : (
                      <AvatarImage src={member?.image} />
                    )}
                    <AvatarFallback>
                      {message.type === 'assistant' ? 'AI' : member?.firstname?.[0]}
                    </AvatarFallback>
                  </Avatar>
                  
                  <div className={`flex-1 max-w-[80%] ${message.type === 'user' ? 'text-right' : ''}`}>
                    <div
                      className={`inline-block p-3 rounded-lg text-sm ${
                        message.type === 'user'
                          ? 'bg-primary text-primary-foreground'
                          : 'bg-muted'
                      }`}
                    >
                      <div className="whitespace-pre-wrap">{message.content}</div>
                    </div>
                    
                    {/* Context Data - Real Members (Simplified for Security) */}
                    {message.contextData?.relevantMembers && (
                      <div className="mt-2 space-y-2">
                        <div className="text-xs font-medium text-muted-foreground mb-1">Membres suggérés :</div>
                        {message.contextData.relevantMembers.map((member: any, index: number) => (
                          <div key={index} className="flex items-center gap-2 p-3 bg-card rounded-lg border shadow-sm">
                            <User className="h-4 w-4 text-primary" />
                            <div className="flex-1">
                              <div className="font-medium text-sm">{member.name}</div>
                              <div className="text-muted-foreground text-xs">
                                {member.role} {member.company && `• ${member.company}`}
                              </div>
                              {member.skills && member.skills.length > 0 && (
                                <div className="flex gap-1 mt-1 flex-wrap">
                                  {member.skills.slice(0, 3).map((skill: string) => (
                                    <Badge key={skill} variant="outline" className="text-xs h-5 px-2">
                                      {skill}
                                    </Badge>
                                  ))}
                                </div>
                              )}
                            </div>
                            <div className="flex flex-col items-end gap-1">
                              <Badge variant="secondary" className="text-xs">
                                {member.relevanceScore}% match
                              </Badge>
                              {(() => {
                                const status = getConnectionStatus(member.user_id);
                                const isConnecting = connectingMembers.has(member.user_id);
                                
                                if (status === 'connected') {
                                  return (
                                    <Button 
                                      variant="outline" 
                                      size="sm" 
                                      className="text-xs h-6 px-2 text-green-600 border-green-200 bg-green-50"
                                      disabled
                                    >
                                      <Check className="h-3 w-3 mr-1" />
                                      Connecté
                                    </Button>
                                  );
                                } else if (status === 'request_sent') {
                                  return (
                                    <Button 
                                      variant="outline" 
                                      size="sm" 
                                      className="text-xs h-6 px-2 text-orange-600 border-orange-200 bg-orange-50"
                                      disabled
                                    >
                                      <ClockIcon className="h-3 w-3 mr-1" />
                                      Demande envoyée
                                    </Button>
                                  );
                                } else if (status === 'request_received') {
                                  return (
                                    <Button 
                                      variant="outline" 
                                      size="sm" 
                                      className="text-xs h-6 px-2 text-blue-600 border-blue-200 bg-blue-50"
                                    >
                                      <Reply className="h-3 w-3 mr-1" />
                                      Répondre
                                    </Button>
                                  );
                                } else {
                                  return (
                                    <Button 
                                      variant="outline" 
                                      size="sm" 
                                      className="text-xs h-6 px-2"
                                      onClick={() => handleConnect(member.user_id, member.name)}
                                      disabled={isConnecting}
                                    >
                                      {isConnecting ? 'Connexion...' : 'Se connecter'}
                                    </Button>
                                  );
                                }
                              })()}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                    
                    {/* Network Analysis */}
                    {message.contextData?.networkAnalysis && (
                      <div className="mt-2 p-2 bg-accent/5 rounded text-xs border">
                        <div className="font-medium mb-1">Analyse de réseau :</div>
                        <div className="text-muted-foreground">
                          {message.contextData.networkAnalysis.totalConnections} connexions • 
                          Force du réseau: {message.contextData.networkAnalysis.networkStrength}/100
                        </div>
                      </div>
                    )}
                    
                    {/* Suggestions */}
                    {message.suggestions && (
                      <div className="mt-2 flex flex-wrap gap-2">
                        {message.suggestions.map((suggestion, index) => (
                          <Button
                            key={index}
                            variant="outline"
                            size="sm"
                            onClick={() => handleSuggestionClick(suggestion)}
                            className="text-xs h-auto py-1 px-2"
                          >
                            {suggestion}
                          </Button>
                        ))}
                      </div>
                    )}
                    
                    <div className="text-xs text-muted-foreground mt-1">
                      {format(message.timestamp, 'HH:mm', { locale: fr })}
                    </div>
                  </div>
                </div>
              ))}
              
              {isLoading && (
                <div className="flex gap-3">
                  <Avatar className="h-8 w-8">
                    <div className="bg-primary/10 text-primary rounded-full w-full h-full flex items-center justify-center">
                      <Bot className="h-4 w-4" />
                    </div>
                  </Avatar>
                  <div className="bg-muted p-3 rounded-lg">
                    <div className="flex space-x-1">
                      <div className="w-2 h-2 bg-primary/40 rounded-full animate-bounce" />
                      <div className="w-2 h-2 bg-primary/40 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }} />
                      <div className="w-2 h-2 bg-primary/40 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }} />
                    </div>
                  </div>
                </div>
              )}
              
              <div ref={messagesEndRef} />
            </div>
            
            {/* Input */}
            <div className="border-t p-4">
              <div className="flex gap-2">
                <Input
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                  placeholder="Demandez-moi quelque chose sur votre networking..."
                  onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
                  className="flex-1"
                />
                <Button
                  variant="outline"
                  size="icon"
                  onClick={handleVoiceToggle}
                  className={isRecording ? 'bg-red-50 text-red-600' : ''}
                >
                  {isRecording ? <MicOff className="h-4 w-4" /> : <Mic className="h-4 w-4" />}
                </Button>
                <Button onClick={handleSendMessage} disabled={!inputValue.trim()}>
                  <Send className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </CardContent>
          </Card>
        </div>

        {/* Insights Panel */}
        <div className="space-y-4 flex flex-col h-full">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <Brain className="h-4 w-4" />
              Insights IA
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {networkingInsights.map((insight, index) => (
              <div
                key={index}
                className="p-3 border rounded-lg hover:bg-accent/5 transition-colors cursor-pointer"
              >
                <div className="flex items-start gap-2">
                  {getInsightIcon(insight.type)}
                  <div className="flex-1">
                    <h4 className="font-medium text-sm">{insight.title}</h4>
                    <p className="text-xs text-muted-foreground mt-1">
                      {insight.description}
                    </p>
                    <div className="flex items-center justify-between mt-2">
                      <Badge variant="secondary" className="text-xs">
                        {insight.confidence}% confiance
                      </Badge>
                      {insight.actionable && (
                        <Button size="sm" variant="ghost" className="h-6 text-xs">
                          Action
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>

        {/* Quick Actions */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Actions Rapides</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            <Button 
              variant="outline" 
              size="sm" 
              className="w-full justify-start"
              onClick={() => setInputValue("Analyse mon réseau et trouve des gaps")}
            >
              <TrendingUp className="h-3 w-3 mr-2" />
              Analyser mon réseau
            </Button>
            <Button 
              variant="outline" 
              size="sm" 
              className="w-full justify-start"
              onClick={() => setInputValue("Qui dois-je contacter cette semaine ?")}
            >
              <Calendar className="h-3 w-3 mr-2" />
              Planning de la semaine
            </Button>
            <Button 
              variant="outline" 
              size="sm" 
              className="w-full justify-start"
              onClick={() => setInputValue("Trouve des événements networking pour moi")}
            >
              <Users className="h-3 w-3 mr-2" />
              Événements recommandés
            </Button>
          </CardContent>
        </Card>
        </div>
      </div>
    </div>
  );
};

export default NetworkingAIAssistant;